import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Fingerprint, KeyRound, ShieldCheck, X } from "lucide-react";
import api from "../lib/api";

const UNLOCK_KEY = "tp_wallet_vault_unlocked";
const CRED_KEY = "tp_wallet_vault_cred_id"; // WebAuthn credential id, per device
const UNLOCK_TTL_MIN = 15; // stay unlocked for 15 minutes
const HIDDEN_LOCK_MS = 60 * 1000; // auto-lock when tab hidden > 60s

/**
 * Double-protection gate for the Passport & ID Vault (passports + driver's
 * licenses). Requires a numeric PIN set once per account. On devices with a
 * platform authenticator (Face ID / Touch ID / Windows Hello), users can opt
 * to unlock with biometrics after PIN setup — the browser handles the sensor
 * prompt via WebAuthn, and we store only the credential id.
 */
function isUnlocked() {
  try {
    const raw = sessionStorage.getItem(UNLOCK_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Date.now() - ts < UNLOCK_TTL_MIN * 60 * 1000;
  } catch { return false; }
}

function markUnlocked() {
  try { sessionStorage.setItem(UNLOCK_KEY, String(Date.now())); } catch { /* ignore */ }
}

function lockVault() {
  try { sessionStorage.removeItem(UNLOCK_KEY); } catch { /* ignore */ }
}

function b64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = ""; for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function biometricEnroll() {
  if (!window.PublicKeyCredential) throw new Error("This browser doesn't support biometrics");
  const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  if (!available) throw new Error("No Face ID / Touch ID / Windows Hello on this device");
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userId = crypto.getRandomValues(new Uint8Array(16));
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge, rp: { name: "TravelerPulse Wallet", id: window.location.hostname },
      user: { id: userId, name: "wallet", displayName: "TravelerPulse Wallet" },
      pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
      timeout: 60000, attestation: "none",
    },
  });
  return b64url(cred.rawId);
}

async function biometricAssert(credentialId) {
  if (!window.PublicKeyCredential || !credentialId) return false;
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const rawId = Uint8Array.from(atob(credentialId.replaceAll("-", "+").replaceAll("_", "/")), (c) => c.charCodeAt(0));
    await navigator.credentials.get({
      publicKey: {
        challenge, timeout: 60000, userVerification: "required",
        allowCredentials: [{ id: rawId, type: "public-key" }],
      },
    });
    return true;
  } catch { return false; }
}

export default function VaultGate({ onUnlock, onClose, title, subtitle, scope }) {
  const [mode, setMode] = useState("loading"); // "setup" | "enter" | "manage"
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = () => {
    setMode("loading");
    api.get("/wallet/vault/status")
      .then(({ data }) => {
        setStatus(data);
        setMode(data.has_pin ? "enter" : "setup");
      })
      .catch(() => setMode("error"));
  };
  useEffect(() => { loadStatus(); }, []);

  const setupPin = async () => {
    if (pin.length < 4) return toast.error("PIN must be at least 4 digits");
    if (pin !== confirmPin) return toast.error("PINs do not match");
    setBusy(true);
    try {
      await api.post("/wallet/vault/pin", { pin });
      markUnlocked();
      toast.success("Vault PIN saved");
      onUnlock();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set PIN"); }
    finally { setBusy(false); }
  };

  const enter = async () => {
    if (pin.length < 4) return toast.error("Enter your PIN");
    setBusy(true);
    try {
      await api.post("/wallet/vault/verify", { pin });
      markUnlocked();
      onUnlock();
    } catch (e) { toast.error(e?.response?.data?.detail || "Incorrect PIN"); }
    finally { setBusy(false); }
  };

  const useBiometrics = async () => {
    const credId = localStorage.getItem(CRED_KEY);
    if (!credId) return toast.error("Enable biometrics from Vault settings first");
    setBusy(true);
    try {
      const ok = await biometricAssert(credId);
      if (!ok) throw new Error("cancelled");
      await api.post("/wallet/vault/biometric/verify", { credential_id: credId });
      markUnlocked();
      onUnlock();
    } catch (e) { toast.error(e?.response?.data?.detail || "Biometric unlock cancelled"); }
    finally { setBusy(false); }
  };

  const enrollBiometrics = async () => {
    setBusy(true);
    try {
      const credId = await biometricEnroll();
      const label = /iPhone|iPad/.test(navigator.userAgent) ? "iPhone Face ID"
        : /Mac/.test(navigator.userAgent) ? "Mac Touch ID"
        : /Windows/.test(navigator.userAgent) ? "Windows Hello" : "This device";
      await api.post("/wallet/vault/biometric/register", { credential_id: credId, label });
      localStorage.setItem(CRED_KEY, credId);
      toast.success(`${label} enabled for the vault`);
      const { data } = await api.get("/wallet/vault/status");
      setStatus(data);
    } catch (e) { toast.error(e?.message || e?.response?.data?.detail || "Could not enroll device"); }
    finally { setBusy(false); }
  };

  const hasEnrolled = status?.biometric_credentials?.length > 0
    && localStorage.getItem(CRED_KEY)
    && status.biometric_credentials.some((c) => c.credential_id === localStorage.getItem(CRED_KEY));

  return (
    <div className="fixed inset-0 z-[9999] bg-zinc-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="vault-gate" onClick={onClose}>
      <div className="bg-white border border-zinc-300 shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-zinc-200">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-sky-700 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading font-900 text-xl">{title || "Passport & ID Vault"}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {mode === "setup" ? (subtitle || "Create a PIN to protect this vault")
                  : "Unlock with your PIN or device biometrics"}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="vault-close" className="text-zinc-400 hover:text-zinc-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {mode === "setup" && (
          <div className="p-5 space-y-3" data-testid="vault-setup">
            <label className="block text-sm"><span className="font-600">New PIN (4–12 digits)</span>
              <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={12} autoFocus
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                data-testid="vault-pin-new" className="mt-1 w-full border border-zinc-300 px-3 py-2 font-mono tracking-widest text-lg" />
            </label>
            <label className="block text-sm"><span className="font-600">Confirm PIN</span>
              <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={12}
                value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                data-testid="vault-pin-confirm" className="mt-1 w-full border border-zinc-300 px-3 py-2 font-mono tracking-widest text-lg" />
            </label>
            <button onClick={setupPin} disabled={busy} data-testid="vault-pin-setup-save"
              className="w-full px-4 py-2.5 bg-sky-600 text-white text-sm font-700 disabled:opacity-60">
              <KeyRound className="w-4 h-4 inline -mt-0.5 mr-1" /> Save vault PIN
            </button>
            <p className="text-xs text-muted-foreground">You'll be able to enable Face ID / Touch ID after saving the PIN.</p>
          </div>
        )}

        {mode === "enter" && (
          <div className="p-5 space-y-3" data-testid="vault-enter">
            <label className="block text-sm"><span className="font-600">Vault PIN</span>
              <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={12} autoFocus
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") enter(); }}
                data-testid="vault-pin-enter" className="mt-1 w-full border border-zinc-300 px-3 py-2 font-mono tracking-widest text-lg" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={enter} disabled={busy} data-testid="vault-unlock-pin"
                className="px-4 py-2.5 bg-sky-600 text-white text-sm font-700 disabled:opacity-60">
                <Lock className="w-4 h-4 inline -mt-0.5 mr-1" /> Unlock
              </button>
              <button onClick={useBiometrics} disabled={busy || !hasEnrolled}
                data-testid="vault-unlock-biometric"
                title={hasEnrolled ? "Unlock with Face ID / Touch ID" : "Enable in vault settings after unlocking"}
                className="px-4 py-2.5 border border-sky-400 text-sky-700 text-sm font-700 disabled:opacity-40">
                <Fingerprint className="w-4 h-4 inline -mt-0.5 mr-1" /> Biometrics
              </button>
            </div>
            <button onClick={enrollBiometrics} disabled={busy}
              data-testid="vault-enroll-biometric"
              className="w-full text-xs text-sky-700 hover:underline mt-1">
              {hasEnrolled ? "Re-enroll biometrics on this device" : "Enable Face ID / Touch ID for this device"}
            </button>
          </div>
        )}

        {mode === "loading" && <p className="p-6 text-sm text-muted-foreground text-center">Loading vault…</p>}

        {mode === "error" && (
          <div className="p-5 space-y-3 text-center" data-testid="vault-error">
            <p className="text-sm text-zinc-800">We couldn't reach the vault right now. Check your connection and try again.</p>
            <button onClick={loadStatus} data-testid="vault-retry"
              className="px-4 py-2 border border-sky-400 text-sky-700 text-sm font-700">
              Retry
            </button>
          </div>
        )}

        <div className="px-5 py-3 border-t border-zinc-100 text-[11px] text-muted-foreground bg-zinc-50">
          Vault stays unlocked for {UNLOCK_TTL_MIN} minutes per session. Biometric credentials never leave your device.
        </div>
      </div>
    </div>
  );
}

export { isUnlocked, markUnlocked, lockVault, HIDDEN_LOCK_MS };
